import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  TextField, 
  Typography, 
  Card, 
  CardContent, 
  Box,
  Button,
  IconButton,
  Chip,
  Alert,
  Divider,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { 
  CalendarMonth, 
  Add, 
  Delete, 
  Info,
  CheckCircle
} from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';

interface AvailabilityPeriod {
  id: string;
  from: string;
  to: string;
  label: string;
}

interface AvailabilityProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

// What we write to the DB when "generally available" is selected.
// Covers a rolling 2-year window from Jan 1 this year — valid, sensible,
// and easy for the band to update later if they want to be specific.
const getGeneralAvailability = () => {
  const currentYear = new Date().getFullYear();
  return {
    from: `${currentYear}-01-01`,
    to: `${currentYear + 2}-12-31`,
    label: 'Generally Available',
  };
};

const Availability: React.FC<AvailabilityProps> = ({ formData, setFormData }) => {
  const [availabilityPeriods, setAvailabilityPeriods] = useState<AvailabilityPeriod[]>([]);
  const [generallyAvailable, setGenerallyAvailable] = useState(false);

  const isTouringStatus = formData.activityStatus === 'touring';

  // Initialize with existing data or sensible defaults
  useEffect(() => {
    if (availabilityPeriods.length === 0) {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      if (formData.availability?.from && formData.availability?.to) {
        const fromStr = formData.availability.from instanceof Date
          ? formData.availability.from.toISOString().split('T')[0]
          : formData.availability.from;
        const toStr = formData.availability.to instanceof Date
          ? formData.availability.to.toISOString().split('T')[0]
          : formData.availability.to;

        // Detect if the existing record was already set to generally available
        const general = getGeneralAvailability();
        if (fromStr === general.from && toStr === general.to) {
          setGenerallyAvailable(true);
        }

        setAvailabilityPeriods([{
          id: '1',
          from: fromStr,
          to: toStr,
          label: 'General Availability'
        }]);
      } else {
        setAvailabilityPeriods([{
          id: '1',
          from: `${currentYear}-01-01`,
          to: `${nextYear}-12-31`,
          label: 'Available Year-Round'
        }]);
      }
    }
  }, []);

  // Sync periods to formData whenever they change
  useEffect(() => {
    if (availabilityPeriods.length > 0) {
      const primaryPeriod = availabilityPeriods[0];
      setFormData(prevData => ({
        ...prevData,
        availability: {
          from: new Date(primaryPeriod.from),
          to: new Date(primaryPeriod.to),
          excludedDates: [],
        },
        availabilityPeriods: availabilityPeriods
      }));
    }
  }, [availabilityPeriods, setFormData]);

  // When "Generally Available" is checked, write the standard dates to the DB
  // and lock the date fields. When unchecked, restore manual editing.
  const handleGenerallyAvailableChange = (checked: boolean) => {
    setGenerallyAvailable(checked);

    if (checked) {
      const general = getGeneralAvailability();
      setAvailabilityPeriods([{
        id: '1',
        from: general.from,
        to: general.to,
        label: general.label,
      }]);
    }
    // If unchecked, leave the dates as-is so the user can edit from that starting point
  };

  const addPeriod = () => {
    const newId = (availabilityPeriods.length + 1).toString();
    const lastPeriod = availabilityPeriods[availabilityPeriods.length - 1];
    
    let defaultFrom = '';
    if (lastPeriod?.to) {
      const nextDay = new Date(lastPeriod.to);
      nextDay.setDate(nextDay.getDate() + 1);
      defaultFrom = nextDay.toISOString().split('T')[0];
    }

    setAvailabilityPeriods(prev => [...prev, {
      id: newId,
      from: defaultFrom,
      to: '',
      label: ''
    }]);
  };

  const removePeriod = (id: string) => {
    if (availabilityPeriods.length > 1) {
      setAvailabilityPeriods(prev => prev.filter(period => period.id !== id));
    }
  };

  const updatePeriod = (id: string, field: keyof AvailabilityPeriod, value: string) => {
    setAvailabilityPeriods(prev => prev.map(period => 
      period.id === id ? { ...period, [field]: value } : period
    ));
  };

  const formatDateRange = (from: string, to: string) => {
    if (!from || !to) return 'Incomplete';
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${fromDate.toLocaleDateString('en-US', options)} - ${toDate.toLocaleDateString('en-US', options)}`;
  };

  const getDaysInPeriod = (from: string, to: string) => {
    if (!from || !to) return 0;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getTotalAvailableDays = () => {
    return availabilityPeriods
      .filter(period => period.from && period.to)
      .reduce((total, period) => total + getDaysInPeriod(period.from, period.to), 0);
  };

  const hasValidPeriods = availabilityPeriods.every(period => period.from && period.to);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" />
            Availability
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Let programmers know when this act is available for bookings. 
            This can be updated at any time from the band profile.
          </Typography>

          {/* Touring status warning — reads from activityStatus already set in BandInfo */}
          {isTouringStatus && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              This act's status is set to <strong>Touring</strong>. Make sure the availability 
              dates below reflect when they will be free for new bookings.
            </Alert>
          )}

          {/* Generally Available checkbox — the fast path for admins */}
          <Box sx={{ 
            mt: 2, 
            mb: 3, 
            p: 2, 
            border: '1px solid',
            borderColor: generallyAvailable ? 'success.main' : 'divider',
            borderRadius: 1,
            bgcolor: generallyAvailable ? 'success.light' : 'background.paper',
            transition: 'all 0.2s',
            opacity: isTouringStatus ? 0.6 : 1,
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={generallyAvailable}
                  onChange={(e) => handleGenerallyAvailableChange(e.target.checked)}
                  color="success"
                  disabled={isTouringStatus}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Generally Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This act is broadly available for bookings. Exact dates can be confirmed 
                    directly with the booking contact.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Specific date periods — shown but read-only when generally available */}
          {!generallyAvailable && (
            <>
              {hasValidPeriods && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>{availabilityPeriods.length}</strong> period{availabilityPeriods.length !== 1 ? 's' : ''} &bull;{' '}
                    <strong>{getTotalAvailableDays()}</strong> total available days
                  </Typography>
                </Alert>
              )}

              <Stack spacing={3} sx={{ mt: 2 }}>
                {availabilityPeriods.map((period, index) => (
                  <Box key={period.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="subtitle1" color="primary">
                        Period {index + 1}
                      </Typography>
                      {availabilityPeriods.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removePeriod(period.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Period Description (Optional)"
                          value={period.label}
                          onChange={(e) => updatePeriod(period.id, 'label', e.target.value)}
                          variant="outlined"
                          fullWidth
                          placeholder="e.g. Summer availability, Post-tour window"
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Available From"
                          type="date"
                          value={period.from}
                          onChange={(e) => updatePeriod(period.id, 'from', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          fullWidth
                          required
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Available Until"
                          type="date"
                          value={period.to}
                          onChange={(e) => updatePeriod(period.id, 'to', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          fullWidth
                          required
                          inputProps={{ min: period.from }}
                        />
                      </Grid>
                    </Grid>

                    {period.from && period.to && (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          {period.label && <strong>{period.label}: </strong>}
                          {formatDateRange(period.from, period.to)}
                          <Chip
                            label={`${getDaysInPeriod(period.from, period.to)} days`}
                            size="small"
                            sx={{ ml: 1 }}
                            color="primary"
                            variant="outlined"
                          />
                        </Typography>
                      </Box>
                    )}

                    {index < availabilityPeriods.length - 1 && (
                      <Divider sx={{ mt: 3 }} />
                    )}
                  </Box>
                ))}
              </Stack>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addPeriod}
                  sx={{ minWidth: 200 }}
                >
                  Add Another Period
                </Button>
              </Box>
            </>
          )}

          {/* Confirmation of what was saved when generally available is checked */}
          {generallyAvailable && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1 }}>
              Saved as generally available. The band can update specific dates from their profile at any time.
            </Alert>
          )}

          <Box sx={{
            mt: 3,
            p: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
          }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Info fontSize="small" sx={{ mt: 0.2, color: 'primary.main', flexShrink: 0 }} />
              <Box>
                <Typography component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  Note:{' '}
                </Typography>
                Availability can be updated at any time from the band profile page. 
                Programmers will contact the booking contact directly to confirm dates.
              </Box>
            </Typography>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default Availability;